// frontend/client/src/components/About.jsx
// Re-themed to match the landing page's brand system exactly (pink/purple/cyan/orange,
// Clash Display + Plus Jakarta Sans, black pill CTAs, glass badges).

import React, { useEffect, useRef } from 'react';
import { useNavigate } from 'react-router-dom';
import { ArrowLeft, Sparkles, Heart, Lightbulb, Rocket, Target, Zap, ArrowRight, BarChart3, Link2 } from 'lucide-react';

const CSS = `
  @import url('https://fonts.googleapis.com/css2?family=Plus+Jakarta+Sans:ital,wght@0,300..800;1,300..800&display=swap');
  @import url('https://api.fontshare.com/v2/css?f[]=clash-display@200,400,700,500,600,300&display=swap');

  .ab { min-height:100vh; background:#F8F9FA; color:#0A0A0A; font-family:'Plus Jakarta Sans',sans-serif; -webkit-font-smoothing:antialiased; position:relative; }
  .ab h1,.ab h2,.ab h3 { font-family:'Clash Display',sans-serif; letter-spacing:-0.01em; }

  .ab-holo-bg { position:absolute; top:0; left:0; width:100%; height:100%; z-index:0; pointer-events:none;
    background:
      radial-gradient(circle at 10% 15%, rgba(255,46,147,0.05) 0%, transparent 45%),
      radial-gradient(circle at 90% 20%, rgba(139,92,246,0.05) 0%, transparent 50%),
      radial-gradient(circle at 60% 80%, rgba(255,138,0,0.03) 0%, transparent 45%);
    filter: blur(60px);
  }
  .ab-content { position:relative; z-index:1; }

  .ab-gt { background:linear-gradient(90deg,#8B5CF6,#FF2E93); -webkit-background-clip:text; background-clip:text; color:transparent; }
  .ab-hero { padding:80px 24px 64px; }
  .ab-sec { max-width:1100px; margin:0 auto; padding:72px 24px; }

  .ab-card { background:rgba(255,255,255,0.95); border:1px solid rgba(255,255,255,1); border-radius:20px; padding:40px;
    box-shadow: 0 25px 50px -12px rgba(0,0,0,0.08), 0 0 0 1px rgba(0,0,0,0.02);
    transition: transform 0.3s cubic-bezier(0.175,0.885,0.32,1.275), box-shadow 0.3s; }
  .ab-card:hover { transform: translateY(-3px); box-shadow: 0 30px 55px -12px rgba(0,0,0,0.12), 0 0 0 1px rgba(0,0,0,0.02); }

  .ab-icon { width:52px;height:52px;border-radius:14px;display:flex;align-items:center;justify-content:center;flex-shrink:0; }
  .ab-diff { background:rgba(255,255,255,0.7); border:1px solid rgba(255,255,255,0.9); border-radius:18px; padding:28px; backdrop-filter:blur(6px); }

  .ab-btn { display:inline-flex;align-items:center;gap:8px;padding:13px 26px;border-radius:9999px;font-family:'Plus Jakarta Sans',sans-serif;font-size:14px;font-weight:700;border:none;cursor:pointer;text-decoration:none;transition:transform .2s,opacity .2s; }
  .ab-btn:hover { transform:scale(1.03); }
  .ab-btn-dark { background:#0A0A0A; color:white; box-shadow:0 10px 20px -6px rgba(0,0,0,0.3); }

  .ab-badge { display:inline-flex;align-items:center;gap:7px;padding:8px 16px;border-radius:9999px;background:rgba(255,255,255,0.8);backdrop-filter:blur(12px);border:1px solid white;font-size:11px;font-weight:700;color:#8B5CF6;text-transform:uppercase;letter-spacing:.08em;font-family:'Plus Jakarta Sans',sans-serif;box-shadow:0 1px 2px rgba(0,0,0,0.03); }

  .ab-reveal { opacity:0; transform:translateX(-24px); transition:opacity 0.6s ease, transform 0.6s ease; }
  .ab-reveal.is-visible { opacity:1; transform:translateX(0); }
  .ab-reveal:nth-child(1) { transition-delay:0s; }
  .ab-reveal:nth-child(2) { transition-delay:0.12s; }
  .ab-reveal:nth-child(3) { transition-delay:0.24s; }
  .ab-reveal:nth-child(4) { transition-delay:0.36s; }
  @media (prefers-reduced-motion: reduce) { .ab-reveal { opacity:1; transform:none; transition:none; } }

  @media(max-width:768px){.ab-hero{padding:56px 20px 40px}.ab-sec{padding:48px 20px}.ab-card{padding:24px}.ab-2col{grid-template-columns:1fr!important}}
`;

export default function About() {
  const navigate = useNavigate();
  const rootRef = useRef(null);

  useEffect(() => {
    const els = rootRef.current ? rootRef.current.querySelectorAll('.ab-reveal') : [];
    const observer = new IntersectionObserver(
      (entries) => {
        entries.forEach((entry) => {
          if (entry.isIntersecting) {
            entry.target.classList.add('is-visible');
            observer.unobserve(entry.target);
          }
        });
      },
      { threshold: 0, rootMargin: '-45% 0px -45% 0px' }
    );
    els.forEach((el) => observer.observe(el));
    return () => observer.disconnect();
  }, []);

  return (
    <div className="ab" id="about" ref={rootRef}>
      <style>{CSS}</style>
      <div className="ab-holo-bg" />

      {/* Hero */}
      <div className="ab-hero ab-content">
        <div style={{ maxWidth: 1100, margin: '0 auto' }}>
          <button onClick={() => navigate('/')} style={{ display: 'inline-flex', alignItems: 'center', gap: 6, fontSize: 13, fontWeight: 700, color: '#8B5CF6', background: 'none', border: 'none', cursor: 'pointer', marginBottom: 32, padding: 0, fontFamily: 'Plus Jakarta Sans, sans-serif' }}>
            <ArrowLeft size={14} /> Back to home
          </button>
          <div className="ab-badge" style={{ marginBottom: 20 }}><Sparkles size={11} /> Our Story</div>
          <h1 style={{ fontSize: 'clamp(36px,5vw,64px)', fontWeight: 700, lineHeight: 1.08, marginBottom: 20, maxWidth: 720 }}>
            Built to give creators{' '}
            <span className="ab-gt">infrastructure, not just tools.</span>
          </h1>
          <p style={{ fontSize: 18, lineHeight: 1.7, color: '#4B5563', maxWidth: 600, margin: 0 }}>
            Infleuince started with a simple observation: every tool solves one piece of the content problem. Nothing connected them. We decided to build the layer that does.
          </p>
        </div>
      </div>

      {/* The problem */}
      <div className="ab-sec ab-content">
        <div className="ab-2col" style={{ display: 'grid', gridTemplateColumns: '1fr 1fr', gap: 32, alignItems: 'start' }}>
          <div>
            <div style={{ display: 'flex', alignItems: 'center', gap: 12, marginBottom: 20 }}>
              <div className="ab-icon" style={{ background: 'linear-gradient(135deg,#FF2E93,#8B5CF6)' }}><Lightbulb size={22} style={{ color: 'white' }} /></div>
              <h2 style={{ fontSize: 28, fontWeight: 700, margin: 0 }}>The observation</h2>
            </div>
            <div style={{ fontSize: 16, lineHeight: 1.75, color: '#4B5563', display: 'flex', flexDirection: 'column', gap: 16 }}>
              <p style={{ margin: 0 }}>Creators don't have a creativity problem. They have a workflow problem. The tools they need to create, optimize, caption, and publish content exist — but they're completely disconnected from each other.</p>
              <p style={{ margin: 0 }}>Buffer handles scheduling. Canva handles design. Jasper handles copy. CapCut handles editing. Every tool requires context-switching, manual export, and rebuilding the same information from scratch at every step.</p>
              <p style={{ margin: 0 }}><span style={{ fontWeight: 700, color: '#0A0A0A' }}>That's the tax on creativity.</span> The hours spent moving between tools, reformatting for platforms, rewriting captions, and manually tracking what worked. We built Infleuince to eliminate that tax entirely.</p>
            </div>
          </div>
          <div className="ab-card" style={{ display: 'flex', flexDirection: 'column', gap: 20 }}>
            {[
              { icon: <Zap size={18} />, color: '#FF2E93', bg: '#FFF0F7', title: 'Disconnected tools', desc: 'Upload in one app, edit in another, caption in a third. Each step loses context.' },
              { icon: <Target size={18} />, color: '#E67E00', bg: '#FFF4E5', title: 'Platform chaos', desc: 'Every platform has different specs, timing, and caption styles. Managing it manually is a full-time job.' },
              { icon: <BarChart3 size={18} />, color: '#8B5CF6', bg: '#F4F0FE', title: 'Analytics as afterthought', desc: 'Performance data sits in a separate dashboard nobody checks until something underperforms.' },
            ].map(({ icon, color, bg, title, desc }) => (
              <div key={title} className="ab-reveal" style={{ display: 'flex', gap: 14, alignItems: 'flex-start' }}>
                <div style={{ width: 38, height: 38, borderRadius: 10, background: bg, display: 'flex', alignItems: 'center', justifyContent: 'center', flexShrink: 0, color }}>{icon}</div>
                <div>
                  <p style={{ fontFamily: 'Clash Display, sans-serif', fontSize: 14, fontWeight: 700, color: '#0A0A0A', margin: '0 0 4px' }}>{title}</p>
                  <p style={{ fontSize: 13, color: '#4B5563', margin: 0, lineHeight: 1.5 }}>{desc}</p>
                </div>
              </div>
            ))}
          </div>
        </div>
      </div>

      {/* What we built */}
      <div className="ab-content" style={{ background: 'rgba(255,255,255,0.5)', borderTop: '1px solid rgba(0,0,0,0.04)', borderBottom: '1px solid rgba(0,0,0,0.04)' }}>
        <div className="ab-sec" style={{ paddingTop: 64, paddingBottom: 64 }}>
          <div style={{ textAlign: 'center', marginBottom: 48 }}>
            <div className="ab-badge" style={{ marginBottom: 16, background: '#FFF0F7', borderColor: '#FFE0EF', color: '#FF2E93' }}><Rocket size={11} /> The solution</div>
            <h2 style={{ fontSize: 'clamp(28px,3.5vw,42px)', fontWeight: 700, margin: '0 0 16px' }}>An orchestration layer, not another feature.</h2>
            <p style={{ fontSize: 16, lineHeight: 1.7, color: '#4B5563', maxWidth: 600, margin: '0 auto' }}>Infleuince connects every step of content creation into one pipeline that remembers your brand, learns from your performance, and gets smarter with every post.</p>
          </div>
          {/* Pipeline */}
          <div style={{ display: 'flex', alignItems: 'center', justifyContent: 'center', gap: 8, flexWrap: 'wrap', marginBottom: 40 }}>
            {['Upload & Edit','Platform Optimize','AI Captions','Feed Preview','Publish','Analytics'].map((step, i, arr) => (
              <React.Fragment key={step}>
                <div style={{ padding: '10px 18px', borderRadius: 9999, background: 'white', border: '1px solid rgba(139,92,246,0.25)', fontSize: 13, fontWeight: 700, color: '#8B5CF6', fontFamily: 'Plus Jakarta Sans, sans-serif', whiteSpace: 'nowrap' }}>{step}</div>
                {i < arr.length - 1 && <ArrowRight size={14} style={{ color: '#C4C9D2', flexShrink: 0 }} />}
              </React.Fragment>
            ))}
          </div>
          <div style={{ display: 'grid', gridTemplateColumns: 'repeat(auto-fit,minmax(260px,1fr))', gap: 20 }}>
            {[
              { title: 'One connected workflow', desc: 'Every tool handoff is automatic. Upload a video, optimize it, add captions, preview, and publish — the context carries the whole way.' },
              { title: 'Brand voice that compounds', desc: 'Every caption, every hook, every post trains your brand model. The longer you use Infleuince, the more it sounds like you.' },
              { title: 'Analytics in the loop', desc: 'Performance data feeds back into the next creation cycle. You see what worked before you start the next post — not after.' },
            ].map(({ title, desc }) => (
              <div key={title} className="ab-diff">
                <div style={{ width: 10, height: 10, borderRadius: 9999, background: 'linear-gradient(135deg,#FF2E93,#8B5CF6)', marginBottom: 14 }} />
                <p style={{ fontFamily: 'Clash Display, sans-serif', fontSize: 16, fontWeight: 700, color: '#0A0A0A', margin: '0 0 8px' }}>{title}</p>
                <p style={{ fontSize: 14, color: '#4B5563', margin: 0, lineHeight: 1.6 }}>{desc}</p>
              </div>
            ))}
          </div>
        </div>
      </div>

      {/* Founder */}
      <div className="ab-sec ab-content">
        <div className="ab-2col" style={{ display: 'grid', gridTemplateColumns: '300px 1fr', gap: 40, alignItems: 'start' }}>
          {/* Profile card */}
          <div className="ab-card" style={{ textAlign: 'center', padding: 32 }}>
            <img
              src="/Headshot.jpg"
              alt="Jennell Greer, Founder and CEO of Infleuince"
              style={{ width: 140, height: 140, borderRadius: '50%', objectFit: 'cover', objectPosition: 'center 30%', margin: '0 auto 18px', display: 'block', border: '3px solid white', boxShadow: '0 8px 24px rgba(139,92,246,0.2)' }}
            />
            <h3 style={{ fontSize: 19, fontWeight: 700, margin: '0 0 3px' }}>Jennell Greer</h3>
            <p style={{ fontSize: 12, fontWeight: 700, color: '#FF2E93', textTransform: 'uppercase', letterSpacing: '0.07em', margin: '0 0 18px' }}>Founder & CEO</p>
            <a
              href="https://www.linkedin.com/in/jennell-g-60b6b8237/"
              target="_blank"
              rel="noopener noreferrer"
              className="ab-btn ab-btn-dark"
              style={{ fontSize: 13, padding: '11px 18px', width: '100%', justifyContent: 'center', marginBottom: 18 }}
            >
              <Link2 size={14} style={{ color: '#FF2E93' }} /> Connect on LinkedIn
            </a>
            <img
              src="/Influential_Woman_Badge.png"
              alt="Influential Women Verified badge"
              style={{ width: 60, height: 60, margin: '0 auto', display: 'block' }}
            />
          </div>

          {/* Story */}
          <div>
            <div style={{ display: 'flex', alignItems: 'center', gap: 12, marginBottom: 20 }}>
              <div className="ab-icon" style={{ background: 'linear-gradient(135deg,#FF2E93,#8B5CF6)' }}><Heart size={22} style={{ color: 'white' }} /></div>
              <h2 style={{ fontSize: 26, fontWeight: 700, margin: 0 }}>Why I built this</h2>
            </div>
            <div style={{ fontSize: 16, lineHeight: 1.75, color: '#4B5563', display: 'flex', flexDirection: 'column', gap: 16 }}>
              <p style={{ margin: 0 }}>I didn't study computer science, and I didn't set out to start a tech company. My background is political science, sales, customer service, and digital marketing — a mix that felt scattered until I started building Infleuince.</p>
              <p style={{ margin: 0 }}>While I was trying to grow my own presence on social media, I felt exactly what most creators feel: the exhausting loop of editing in one app, captioning in another, publishing in a third, and starting from scratch every time. The ideas were never the hard part. The workflow was.</p>
              <p style={{ margin: 0 }}>So I taught myself to build the fix. I picked up full-stack development and learned to work with AI systems, and turned that frustration into the product I wished existed — one that remembers your brand, handles the busywork, and gets a little smarter with every post.</p>
              <p style={{ margin: 0 }}>I'm still the one building this day to day, which means every decision comes from actually using it — not guessing what creators need.</p>
            </div>
          </div>
        </div>
      </div>

      {/* Mission CTA */}
      <div className="ab-content" style={{ background: 'linear-gradient(135deg,#8B5CF6 0%,#FF2E93 100%)', padding: '72px 24px' }}>
        <div style={{ maxWidth: 760, margin: '0 auto', textAlign: 'center', color: 'white' }}>
          <div style={{ display: 'inline-flex', alignItems: 'center', gap: 7, padding: '8px 16px', borderRadius: 9999, background: 'rgba(255,255,255,0.15)', fontSize: 11, fontWeight: 700, textTransform: 'uppercase', letterSpacing: '0.08em', fontFamily: 'Plus Jakarta Sans, sans-serif', marginBottom: 24, backdropFilter: 'blur(8px)', border: '1px solid rgba(255,255,255,0.25)' }}>
            <Heart size={11} /> Mission
          </div>
          <h2 style={{ fontSize: 'clamp(28px,3.5vw,42px)', fontWeight: 700, lineHeight: 1.1, marginBottom: 20 }}>Make content creation feel like creative work again.</h2>
          <p style={{ fontSize: 17, lineHeight: 1.7, opacity: 0.92, marginBottom: 36, maxWidth: 580, margin: '0 auto 36px' }}>The best creators don't win because they work harder. They win because they've built systems that let them focus on what only they can do. Infleuince is that system.</p>
          <div style={{ display: 'flex', gap: 12, justifyContent: 'center', flexWrap: 'wrap' }}>
            <a href="/#waitlist" className="ab-btn" style={{ background: '#0A0A0A', color: 'white' }}>Join the waitlist <ArrowRight size={16} /></a>
            <a href="/contact-us" className="ab-btn" style={{ background: 'rgba(255,255,255,0.15)', border: '1px solid rgba(255,255,255,0.3)', color: 'white' }}>Get in touch <Link2 size={16} /></a>
          </div>
        </div>
      </div>
    </div>
  );
}